import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { MobileNav } from './MobileNav';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useTrialStatus } from '@/features/trial/useTrialStatus';
import { UpgradeDialog } from '@/features/trial/UpgradeDialog';
import { useUpgradeDialogStore } from '@/stores/upgradeDialog';
import { useMe } from '@/features/session/api';
import { useLocaleStore } from '@/stores/locale';
import { SUPPORTED_LOCALES, type Locale } from '@/lib/i18n';
import { TourOverlay } from '@/features/tour/TourOverlay';
import { useTourStore } from '@/features/tour/store';
import { useAutoPageTour } from '@/features/tour/useAutoPageTour';
import { AssistantLauncher } from '@/features/assistant/AssistantLauncher';
import { useLinkPendingDirectoryClaim } from '@/features/workspace/useLinkPendingDirectoryClaim';

const TITLE_BY_PREFIX: [string, string][] = [
  ['/leads', 'nav.leads'],
  ['/quotes', 'nav.quotes'],
  ['/jobs', 'nav.jobs'],
  ['/customers', 'nav.customers'],
  ['/inventory', 'nav.inventory'],
  ['/calendar', 'nav.calendar'],
  ['/reports', 'nav.reports'],
  ['/integrations', 'nav.integrations'],
  ['/pricing', 'nav.pricing'],
  ['/settings', 'nav.settings'],
  ['/workspace', 'nav.workspace'],
];

function titleKeyFor(pathname: string): string {
  const match = TITLE_BY_PREFIX.find(([prefix]) => pathname.startsWith(prefix));
  return match ? match[1] : 'nav.dashboard';
}

const SESSION_KEY = 'wf-upgrade-dialog-shown';

// Once per session, adopt the language saved on the user's profile so a
// fresh login on a new device picks up their account preference instead of
// staying on the browser-detected/local default.
function useSyncLocaleFromProfile() {
  const { data: me } = useMe();
  const { locale, setLocale } = useLocaleStore();
  const synced = useRef(false);
  useEffect(() => {
    if (synced.current || !me?.locale) return;
    synced.current = true;
    if (SUPPORTED_LOCALES.includes(me.locale as Locale) && me.locale !== locale) setLocale(me.locale as Locale);
  }, [me, locale, setLocale]);
}

export function AppShell() {
  const { t } = useTranslation();
  const location = useLocation();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const { expired } = useTrialStatus(workspaceId);
  const { open, setOpen } = useUpgradeDialogStore();
  useSyncLocaleFromProfile();
  useLinkPendingDirectoryClaim(workspaceId);
  const { autoStarted, setAutoStarted, startTour } = useTourStore();
  useAutoPageTour();

  useEffect(() => {
    if (!sessionStorage.getItem('wf-start-tour')) return;
    sessionStorage.removeItem('wf-start-tour');
    if (autoStarted) return;
    setAutoStarted(true);
    startTour('getting-started');
  }, [autoStarted, setAutoStarted, startTour]);

  // §23.4 - the upgrade dialog auto-opens once per browser session on expiry;
  // every subsequent 402 PLAN_EXPIRED (handled globally in query-client.ts)
  // re-opens it regardless of this flag.
  useEffect(() => {
    if (!expired) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    sessionStorage.setItem(SESSION_KEY, '1');
    setOpen(true);
  }, [expired, setOpen]);

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col pt-safe md:pt-0">
        {expired ? (
          <div className="flex-none bg-danger px-6 py-2 text-center text-sm font-semibold text-white">
            {t('trialExpiredBanner')}
          </div>
        ) : null}
        <Topbar title={t(titleKeyFor(location.pathname))} />
        <main className="min-h-0 flex-1 overflow-auto overscroll-contain pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-0">
          <Outlet />
        </main>
      </div>
      <MobileNav />
      <UpgradeDialog workspaceId={workspaceId} role={membership?.role ?? ''} open={open} />
      <TourOverlay />
      <AssistantLauncher />
    </div>
  );
}
